import { useCallback, useEffect, useRef, useState } from 'react';
import { EVENT_KIND_LABEL, type EquipmentFileDto, type EventKind, type MyShiftDto, type TimeEventDto } from '@app/shared';
import { api } from '../api';
import { platform } from '../platform';
import { useApp } from '../state';
import { clock, Icon, Sheet } from '../ui';
import { AuthImg } from './AuthMedia';

/** Часы:минуты:секунды с момента. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}
export const hms = (from: string, to: number) => {
  const s = Math.max(0, Math.floor((to - new Date(from).getTime()) / 1000));
  return `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
export const mins = (from: string, to: string | null) => {
  const m = Math.round(((to ? new Date(to).getTime() : Date.now()) - new Date(from).getTime()) / 60000);
  return m >= 60 ? `${Math.floor(m / 60)} ч ${m % 60} мин` : `${m} мин`;
};

const START: EventKind[] = ['changeover', 'production', 'packing', 'check', 'other'];

/** Фото к событию: снять или выбрать — загрузка без привязки к машине (видит руководитель в журнале). */
function PhotoPicker({ value, onChange }: { value: EquipmentFileDto[]; onChange: (f: EquipmentFileDto[]) => void }) {
  const { fail } = useApp();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div className="row wrap" style={{ gap: 8 }}>
      {value.map((f) => (
        <span key={f.id} className="check-thumb">
          <span className="photo">
            <AuthImg path={f.url} />
          </span>
          <button type="button" className="icon-btn" aria-label="Убрать фото" onClick={() => onChange(value.filter((x) => x.id !== f.id))}>
            <Icon name="close" size={14} />
          </button>
        </span>
      ))}
      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (input.current) input.current.value = '';
          if (!f) return;
          setBusy(true);
          try {
            onChange([...value, await api.uploadSharedFile(f, 'Фото к событию', () => {})]);
          } catch (err) {
            fail(err);
          } finally {
            setBusy(false);
          }
        }}
      />
      <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => input.current?.click()}>
        <Icon name="camera" size={16} />
        {busy ? 'Загружаю…' : 'Фото'}
      </button>
    </div>
  );
}

/**
 * Моя смена — панель оператора: задачи смены с выработкой (частично: 270 из 500), таймер события
 * («Переналадка машины 1» — 8:15…8:43), время без события → простой с типом, журнал, закрытие смены.
 */
export function MyShift() {
  const { fail, toast, open } = useApp();
  const [d, setD] = useState<MyShiftDto | null>(null);
  const [start, setStart] = useState<EventKind | null>(null);
  const [finish, setFinish] = useState<TimeEventDto | null>(null);
  const [down, setDown] = useState(false);
  // Закрытие: смену и задачи запоминаем при открытии — после закрытия смена из панели уходит, а шаг «проверка» остаётся.
  const [close, setClose] = useState<{ id: string; tasks: MyShiftDto['tasks'] } | null>(null);
  const [qty, setQty] = useState<Record<string, string>>({});
  const [mode, setMode] = useState<Record<string, 'hour' | 'total'>>({});
  const now = useNow();

  const load = useCallback(async () => {
    try {
      setD(await api.myShift());
    } catch (e) {
      fail(e);
    }
  }, [fail]);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  async function addOutput(taskId: string) {
    const n = Number((qty[taskId] ?? '').replace(',', '.'));
    if (!Number.isFinite(n) || !(qty[taskId] ?? '').trim()) return fail(new Error('Сколько штук?'));
    try {
      const r = await api.output({ taskId, qty: n, mode: mode[taskId] ?? 'hour' });
      platform.haptic('success');
      toast(`Записано: +${r.added}. В смене ${r.doneShift}`);
      setQty((q) => ({ ...q, [taskId]: '' }));
      await load();
    } catch (e) {
      fail(e);
    }
  }

  if (!d) return <div className="skeleton" />;
  const sh = d.shift;
  return (
    <div className="stack">
      <div className="card flat">
        {sh ? (
          <>
            <b>
              Смена {sh.day.slice(8, 10)}.{sh.day.slice(5, 7)}, {clock(sh.startAt)}–{clock(sh.endAt)}
            </b>
            <div className="small muted">
              {sh.status === 'closed' ? 'закрыта' : now < new Date(sh.startAt).getTime() ? 'ещё не началась' : now > new Date(sh.endAt).getTime() ? 'время вышло — закройте смену' : `идёт ${hms(sh.startAt, now)}`}
            </div>
          </>
        ) : (
          <span className="muted">Смены пока нет. Задачи появятся здесь, когда руководитель назначит вас на смену.</span>
        )}
      </div>

      {/* Таймер: текущее событие или время без события */}
      {d.current ? (
        <div className="timer-card running">
          <span className="small">{EVENT_KIND_LABEL[d.current.kind]}{d.current.taskTitle ? ` · ${d.current.taskTitle}` : ''}{d.current.equipmentName ? ` · ${d.current.equipmentName}` : ''}</span>
          <span className="timer">{hms(d.current.startedAt, now)}</span>
          <span className="small muted">с {clock(d.current.startedAt)}</span>
          <button type="button" className="btn btn-primary btn-block" onClick={() => setFinish(d.current)}>
            <Icon name="check" size={18} />
            Закончить
          </button>
        </div>
      ) : (
        d.idleSince && (
          <div className="timer-card idle">
            <span className="small">Без отметки события</span>
            <span className="timer">{hms(d.idleSince, now)}</span>
            <span className="small muted">с {clock(d.idleSince)}</span>
            <button type="button" className="btn btn-outline btn-block" onClick={() => setDown(true)}>
              <Icon name="alert" size={18} />
              Отметить простой
            </button>
          </div>
        )
      )}

      <div className="start-grid" role="group" aria-label="Начать событие">
        {START.map((k) => (
          <button key={k} type="button" className="btn btn-ghost btn-sm" onClick={() => setStart(k)}>
            <Icon name="play" size={14} />
            {EVENT_KIND_LABEL[k]}
          </button>
        ))}
      </div>

      <h2 className="section-title">Мои задачи · {d.tasks.length}</h2>
      {d.tasks.length === 0 && <p className="small muted">Задач на смену нет.</p>}
      {d.tasks.map((t) => (
        <div key={t.id} className="card stack" style={{ gap: 8 }}>
          <div>
            <b>{t.sku ?? t.title}</b>
            {t.name && t.name !== t.sku && <div className="small muted">{t.name}</div>}
          </div>
          <div className="small">
            В смене: <b>{t.doneShift.toLocaleString('ru-RU')}</b> · всего {t.done.toLocaleString('ru-RU')}
            {t.qty !== null ? ` из ${t.qty.toLocaleString('ru-RU')}` : ''}
            {t.equipmentName ? ` · ${t.equipmentName}` : ''}
          </div>
          {t.qty !== null && (
            <div className="progress">
              <i style={{ width: `${Math.round(Math.min(1, t.done / t.qty) * 100)}%` }} />
            </div>
          )}
          <div className="seg" role="group" aria-label="Как считать">
            <button type="button" aria-pressed={(mode[t.id] ?? 'hour') === 'hour'} onClick={() => setMode((m) => ({ ...m, [t.id]: 'hour' }))}>
              За час
            </button>
            <button type="button" aria-pressed={mode[t.id] === 'total'} onClick={() => setMode((m) => ({ ...m, [t.id]: 'total' }))}>
              Всего с начала смены
            </button>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <input
              className="input grow"
              inputMode="decimal"
              placeholder={(mode[t.id] ?? 'hour') === 'hour' ? 'Сделано за час, шт.' : 'Всего на счётчике, шт.'}
              aria-label={`Выработка: ${t.sku ?? t.title}`}
              value={qty[t.id] ?? ''}
              onChange={(e) => setQty((q) => ({ ...q, [t.id]: e.target.value }))}
            />
            <button type="button" className="btn btn-primary" onClick={() => void addOutput(t.id)}>
              Отметить
            </button>
          </div>
        </div>
      ))}

      <h2 className="section-title">Журнал времени</h2>
      <EventList events={d.events} now={now} />

      {sh && sh.status === 'open' && (
        <button type="button" className="btn btn-outline btn-block" onClick={() => setClose({ id: sh.id, tasks: d.tasks })}>
          Закрыть смену
        </button>
      )}

      {start && (
        <StartSheet
          kind={start}
          tasks={d.tasks}
          onClose={() => setStart(null)}
          onDone={async () => {
            setStart(null);
            await load();
          }}
        />
      )}
      {finish && (
        <FinishSheet
          ev={finish}
          onClose={() => setFinish(null)}
          onDone={async () => {
            setFinish(null);
            await load();
          }}
        />
      )}
      {down && (
        <DowntimeSheet
          reasons={d.downtimeReasons}
          since={d.idleSince}
          onClose={() => setDown(false)}
          onDone={async () => {
            setDown(false);
            await load();
          }}
        />
      )}
      {close && (
        <CloseSheet
          shiftId={close.id}
          tasks={close.tasks}
          onClose={() => setClose(null)}
          onCheck={(checkId) => open({ name: 'check', id: checkId })}
          onDone={load}
        />
      )}
    </div>
  );
}

export function EventList({ events, now, who = false }: { events: TimeEventDto[]; now: number; who?: boolean }) {
  const [big, setBig] = useState<string | null>(null);
  if (!events.length) return <p className="small muted">Событий пока нет.</p>;
  return (
    <div className="list">
      {events.map((e) => (
        <div key={e.id} className={`item ev-${e.kind}`} style={{ alignItems: 'flex-start' }}>
          <span className="grow stack" style={{ gap: 2 }}>
            <span className="title">
              {e.kind === 'downtime' ? `Простой: ${e.reason ?? ''}` : EVENT_KIND_LABEL[e.kind]}
              {e.taskTitle ? ` · ${e.taskTitle}` : ''}
              {e.equipmentName ? ` · ${e.equipmentName}` : ''}
            </span>
            <span className="sub">
              {who ? `${e.employeeName} · ` : ''}
              {clock(e.startedAt)}–{e.endedAt ? clock(e.endedAt) : 'идёт'} · {e.endedAt ? mins(e.startedAt, e.endedAt) : hms(e.startedAt, now)}
            </span>
            {e.comment && <span className="sub">{e.comment}</span>}
            {e.files.length > 0 && (
              <span className="row wrap" style={{ gap: 6 }}>
                {e.files.map((f) => (
                  <button key={f.id} type="button" className="check-thumb plain" onClick={() => setBig(f.url)} aria-label="Фото к событию">
                    <span className="photo">
                      <AuthImg path={f.url} />
                    </span>
                  </button>
                ))}
              </span>
            )}
          </span>
        </div>
      ))}
      {big && (
        <Sheet title="Фото" onClose={() => setBig(null)}>
          <AuthImg path={big} style={{ width: '100%', borderRadius: 12 }} />
        </Sheet>
      )}
    </div>
  );
}

function StartSheet({ kind, tasks, onClose, onDone }: { kind: EventKind; tasks: MyShiftDto['tasks']; onClose: () => void; onDone: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [taskId, setTaskId] = useState(tasks[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api.startEvent({ kind, taskId: taskId || null, title: title.trim() || null });
      platform.haptic('success');
      toast(`${EVENT_KIND_LABEL[kind]}: пошёл таймер`);
      await onDone();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet title={`Начать: ${EVENT_KIND_LABEL[kind]}`} onClose={onClose}>
      {tasks.length > 0 && (
        <div className="field">
          <label htmlFor="evt">Задача</label>
          <select id="evt" className="input" value={taskId} onChange={(e) => setTaskId(e.target.value)}>
            <option value="">— без задачи</option>
            {tasks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.sku ?? t.title}
                {t.equipmentName ? ` · ${t.equipmentName}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="field">
        <label htmlFor="evn">Что именно (необязательно)</label>
        <input id="evn" className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Например: переналадка машины 1" />
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        Если сейчас идёт другое событие, оно закончится в этот момент.
      </p>
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void go()}>
        <Icon name="play" size={18} />
        Начать
      </button>
    </Sheet>
  );
}

function FinishSheet({ ev, onClose, onDone }: { ev: TimeEventDto; onClose: () => void; onDone: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<EquipmentFileDto[]>([]);
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api.finishEvent(ev.id, { comment: comment.trim() || null, fileIds: files.map((f) => f.id) });
      platform.haptic('success');
      toast('Событие закончено');
      await onDone();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet title={`Закончить: ${EVENT_KIND_LABEL[ev.kind]}`} onClose={onClose}>
      <div className="field">
        <label htmlFor="evc">Комментарий</label>
        <textarea id="evc" className="input" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Например: долго настраивал высоту упора" />
      </div>
      <PhotoPicker value={files} onChange={setFiles} />
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void go()}>
        Закончить
      </button>
    </Sheet>
  );
}

function DowntimeSheet({ reasons, since, onClose, onDone }: { reasons: string[]; since: string | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<EquipmentFileDto[]>([]);
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!reason) return fail(new Error('Выберите тип простоя'));
    setBusy(true);
    try {
      await api.downtime({ reason, comment: comment.trim() || null, fileIds: files.map((f) => f.id) });
      platform.haptic('success');
      toast('Простой отмечен');
      await onDone();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet title="Простой" onClose={onClose}>
      {since && <p className="small muted" style={{ margin: 0 }}>С {clock(since)} до сейчас.</p>}
      <div className="chips">
        {reasons.map((r) => (
          <button key={r} type="button" className="chip" aria-pressed={reason === r} onClick={() => setReason(r)}>
            {r}
          </button>
        ))}
      </div>
      <div className="field">
        <label htmlFor="dtc">Комментарий</label>
        <textarea id="dtc" className="input" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
      </div>
      <PhotoPicker value={files} onChange={setFiles} />
      <button type="button" className="btn btn-primary btn-block" disabled={busy || !reason} onClick={() => void go()}>
        Отметить простой
      </button>
    </Sheet>
  );
}

/** Закрытие смены: подтвердить количество по задачам, затем — проверка оборудования после смены. */
function CloseSheet({ shiftId, tasks, onClose, onCheck, onDone }: { shiftId: string; tasks: MyShiftDto['tasks']; onClose: () => void; onCheck: (checkId: string) => void; onDone: () => Promise<void> }) {
  const { fail, toast } = useApp();
  const [totals, setTotals] = useState<Record<string, string>>(() => Object.fromEntries(tasks.map((t) => [t.id, String(t.doneShift)])));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [machines, setMachines] = useState<{ id: string; name: string }[] | null>(null);
  async function go() {
    setBusy(true);
    try {
      const r = await api.closeShift(
        shiftId,
        tasks.map((t) => ({ taskId: t.id, qty: Number((totals[t.id] ?? '0').replace(',', '.')) || 0 })),
        note.trim() || null,
      );
      platform.haptic('success');
      toast('Смена закрыта');
      setMachines(r.equipment);
      await onDone();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }
  async function check(eqId: string) {
    try {
      const c = await api.startCheck(eqId, 'after');
      onClose();
      onCheck(c.id);
    } catch (e) {
      fail(e);
    }
  }
  if (machines) {
    return (
      <Sheet title="Проверка оборудования" onClose={onClose}>
        <p className="small muted" style={{ margin: 0 }}>
          Смена закрыта. Пройдите проверку «По окончании рабочей смены» на машинах смены.
        </p>
        {machines.length === 0 && <p className="small muted">Машины в смене не указаны — проверку можно пройти со страницы машины.</p>}
        {machines.map((m) => (
          <button key={m.id} type="button" className="btn btn-primary btn-block" onClick={() => void check(m.id)}>
            <Icon name="tasks" size={18} />
            Проверка после смены: {m.name}
          </button>
        ))}
        <button type="button" className="btn btn-ghost btn-block" onClick={onClose}>
          Позже
        </button>
      </Sheet>
    );
  }
  return (
    <Sheet title="Закрыть смену" onClose={onClose}>
      <p className="small muted" style={{ margin: 0 }}>
        Проверьте, сколько сделано за смену. Если отмечали неточно — поправьте, разница запишется поправкой.
      </p>
      {tasks.map((t) => (
        <div key={t.id} className="field">
          <label htmlFor={`cl-${t.id}`}>
            {t.sku ?? t.title}
            {t.qty !== null ? ` (задание ${t.qty})` : ''}
          </label>
          <input id={`cl-${t.id}`} className="input" inputMode="decimal" value={totals[t.id] ?? ''} onChange={(e) => setTotals((x) => ({ ...x, [t.id]: e.target.value }))} />
        </div>
      ))}
      <div className="field">
        <label htmlFor="cln">Комментарий к смене</label>
        <textarea id="cln" className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void go()}>
        Подтвердить и закрыть смену
      </button>
    </Sheet>
  );
}
